import { useNavigate } from 'react-router-dom';
import { Paperclip, Sparkles, Maximize2, Eye, CalendarClock, Flag } from 'lucide-react';
import { AttachmentDisplay } from '@/components/ui/AttachmentDisplay';
import { Avatar, AvatarGroup } from '@/components/ui/Avatar';
import { StatusDropdown, DiscussionStatus } from '@/components/ui/StatusDropdown';
import { DiscussionSeenBy } from '@/lib/api';
import { cn, formatRelativeTime } from '@/lib/utils';

export interface Discussion {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  discussionStatus?: DiscussionStatus | null;
  aiSummary?: string | null;
  author: {
    id: string;
    name: string;
    email?: string;
    avatarUrl?: string;
  };
  participants?: Array<{
    id: string;
    name: string;
    avatarUrl?: string;
  }>;
  lastActivityAt?: string | null;
  lastReply?: {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; name: string; avatarUrl?: string };
    attachments?: Array<{ id: string; fileName: string; mimeType: string; fileSize: number; createdAt: string }>;
  } | null;
  _count?: {
    replies: number;
    attachments?: number;
  };
  attachments?: Array<{ id: string }>;
  /** Read receipts — present only in agency-admin responses */
  seenBy?: DiscussionSeenBy[];
  seenCount?: number;
  /** Non-null while composed but not yet published */
  scheduledFor?: string | null;
  /** Non-null when a project admin marked it Important (pinned to the top) */
  importantAt?: string | null;
}

interface DiscussionCardProps {
  discussion: Discussion;
  projectId: string;
  isAgencyMember?: boolean;
  onStatusChange?: (discussionId: string, status: DiscussionStatus) => void;
  className?: string;
  discussionType?: string;
  /** When provided, opens the thread as a takeover modal instead of navigating. */
  onOpen?: (discussionId: string) => void;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/[#*_`]/g, '').trim();
}

export function DiscussionCard({
  discussion,
  projectId,
  isAgencyMember = false,
  onStatusChange,
  className,
  discussionType,
  onOpen,
}: DiscussionCardProps) {
  const navigate = useNavigate();
  const replyCount = discussion._count?.replies || 0;
  const attachmentCount = discussion._count?.attachments || discussion.attachments?.length || 0;
  const hasLastReply = replyCount > 0 && !!discussion.lastActivityAt;

  // When replies exist, show last reply content; otherwise show AI summary or content preview
  const hasReplies = replyCount > 0;
  const lastReplyContent = discussion.lastReply ? stripHtml(discussion.lastReply.content) : null;
  const contentPreview = stripHtml(discussion.content);
  const preview = hasReplies ? lastReplyContent : (discussion.aiSummary || contentPreview);
  const hasAiSummary = !hasReplies && !!discussion.aiSummary;

  // Format the author name (First + Last Initial)
  const authorName = discussion.author?.name || 'Unknown';
  const nameParts = authorName.split(' ');
  const formattedAuthorName = nameParts.length > 1
    ? `${nameParts[0]} ${nameParts[1][0]}.`
    : nameParts[0];

  const discussionUrl = discussionType
    ? `/projects/${projectId}/custom/${discussionType}/${discussion.id}`
    : `/projects/${projectId}/discussions/${discussion.id}`;

  const open = () => (onOpen ? onOpen(discussion.id) : navigate(discussionUrl));

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open if clicking on the status dropdown or attachments
    if ((e.target as HTMLElement).closest('[data-status-dropdown]') ||
        (e.target as HTMLElement).closest('[data-attachments]')) {
      return;
    }
    open();
  };

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'group relative bg-white rounded-xl border border-grey-200/50 shadow-card p-5 transition-all hover:shadow-card-hover cursor-pointer',
        // Important: an amber edge and wash, so it reads as pinned at a glance
        discussion.importantAt && 'border-warning/40 border-l-4 border-l-warning bg-warning/5',
        className
      )}
    >

      {/* Header Row: Avatar, Title, Status */}
      <div className="flex items-start gap-3">
        <Avatar
          src={discussion.author?.avatarUrl}
          userId={discussion.author?.id}
          name={authorName}
          size="md"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {discussion.importantAt && (
                <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-secondary">
                  <Flag className="h-3 w-3 fill-warning text-warning" aria-hidden />
                  Important
                </span>
              )}
              <h3 className="font-semibold text-secondary hover:text-accent transition-colors line-clamp-1">
                {discussion.title || 'Untitled Discussion'}
              </h3>
              <p className="text-xs text-grey-400 mt-0.5">
                {discussion.scheduledFor ? (
                  <span className="inline-flex items-center gap-1 text-accent font-medium">
                    <CalendarClock className="w-3.5 h-3.5" />
                    Scheduled for {new Date(discussion.scheduledFor).toLocaleString()} • by {formattedAuthorName}
                  </span>
                ) : (
                  <>Started by {formattedAuthorName} • {formatRelativeTime(new Date(discussion.createdAt))}</>
                )}
              </p>
            </div>

            {/* Status Dropdown (Agency Only) */}
            {isAgencyMember && (
              <div data-status-dropdown>
                <StatusDropdown
                  status={(discussion.discussionStatus || 'open') as DiscussionStatus}
                  onChange={(newStatus) => {
                    onStatusChange?.(discussion.id, newStatus);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Text (AI Summary, Content Preview, or Last Reply with Attachments) */}
      {(preview || (hasReplies && discussion.lastReply?.attachments?.length)) ? (
        <div className="mt-4 mb-5 pl-13">
          {hasAiSummary && (
            <div className="flex items-center gap-1.5 text-xs text-accent/70 mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>AI Summary</span>
            </div>
          )}
          {hasReplies && discussion.lastReply?.author && (
            <div className="text-xs font-medium uppercase tracking-wide text-grey-400 mb-1.5">
              {discussion.lastReply.author.name.split(' ')[0]} replied
            </div>
          )}
          {hasReplies && preview && (
            <p className="text-[17px] text-secondary leading-relaxed line-clamp-3">
              {preview}
            </p>
          )}
          {!hasReplies && preview && (
            <p className={cn(
              'text-[17px] text-secondary leading-relaxed',
              !hasAiSummary && 'line-clamp-8',
            )}>
              {preview}
            </p>
          )}
          {hasReplies && discussion.lastReply?.attachments && discussion.lastReply.attachments.length > 0 && (
            <div data-attachments className={cn(preview && 'mt-3')}>
              <AttachmentDisplay attachments={discussion.lastReply.attachments as any} />
            </div>
          )}
        </div>
      ) : null}

      {/* Footer Row: Participants, Attachments, Last Activity, Reply Count */}
      <div className="relative flex items-center justify-between pl-13 pt-2">
        {onOpen && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center pt-2 text-xs text-grey-300 opacity-0 transition-opacity group-hover:opacity-100">
            Click to expand
          </span>
        )}
        <div className="flex items-center gap-3">
          {/* Participant Avatars */}
          {discussion.participants && discussion.participants.filter(p => p != null).length > 0 && (
            <AvatarGroup
              users={discussion.participants.filter(p => p != null).map(p => ({
                name: p.name || 'Unknown',
                avatarUrl: p.avatarUrl,
              }))}
              max={4}
              size="xs"
            />
          )}

          {/* Attachment Count */}
          {attachmentCount > 0 && (
            <div className="flex items-center gap-1 text-grey-400 text-xs">
              <Paperclip className="w-3.5 h-3.5" />
              <span>{attachmentCount}</span>
            </div>
          )}

          {/* Read receipts — only ever present for agency admins */}
          {typeof discussion.seenCount === 'number' && discussion.seenCount > 0 && (
            <div
              className="flex items-center gap-1 text-grey-400 text-xs"
              title={(discussion.seenBy ?? []).map((s) => s.name ?? 'Unknown').join('\n')}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>{discussion.seenCount}</span>
            </div>
          )}
        </div>

        {/* Right side: Last reply + count */}
        <div className="flex items-center gap-2 text-xs text-grey-400">
          {hasLastReply && (
            <span>Last reply {formatRelativeTime(new Date(discussion.lastActivityAt!))}</span>
          )}
          {replyCount > 0 && (
            <>
              {hasLastReply && <span>•</span>}
              <span className="text-accent font-medium">
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
